import { useAutoFitPdfHeight } from "@/components/Pdf/useAutoFitPdfHeight";
import { BodyText, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ExternalLink, RotateCcw, ZoomIn, ZoomOut } from "lucide-react";
import { type UseImageViewerOptions, useImageViewer } from "./useImageViewer";

export interface ImageViewerProps
  extends Omit<UseImageViewerOptions, "containerRef"> {
  className?: string;
}

export const ImageViewer = ({
  src,
  initialScale,
  minScale,
  maxScale,
  className,
}: ImageViewerProps) => {
  const {
    containerRef: outerRef,
    pageHeight,
    containerWidth,
  } = useAutoFitPdfHeight({
    bottomMarginPx: 128,
    minHeight: 384,
  });
  const { scale, zoomIn, zoomOut, resetZoom, openInNewTab, computedSize } =
    useImageViewer({
      src,
      initialScale,
      minScale,
      maxScale,
      containerWidth,
      containerHeight: pageHeight,
    });

  const getTopOffset = () => {
    if (!computedSize) return 0;
    return Math.max(0, pageHeight / 2 - computedSize.height / 2);
  };

  return (
    <div
      ref={outerRef}
      className={cn("relative w-full", className)}
      style={{ height: pageHeight }}
    >
      <div className="absolute right-3 top-3 z-10">
        <Button
          variant="secondary"
          size="md"
          onClick={openInNewTab}
          className="shadow-md"
          leftIcon={ExternalLink}
        >
          Open in New Tab
        </Button>
      </div>
      {scale !== 1 && (
        <div className="absolute right-3 bottom-16 z-10 bg-black/70 px-3 py-1.5 rounded-lg">
          <BodyText
            size="sm"
            textColor="white"
          >{`${(scale * 100).toFixed(0)}%`}</BodyText>
        </div>
      )}
      <div className="absolute right-3 bottom-3 z-10 flex gap-2">
        {scale !== 1 && (
          <Button
            variant="secondary"
            size="icon"
            onClick={resetZoom}
            className="shadow-lg"
            aria-label="Reset zoom"
          >
            <RotateCcw className="w-5 h-5 text-button-secondary" />
          </Button>
        )}
        <Button
          variant="primary"
          size="icon"
          onClick={zoomOut}
          className="shadow-lg"
          aria-label="Zoom out"
        >
          <ZoomOut className="w-5 h-5 text-white" />
        </Button>
        <Button
          variant="primary"
          size="icon"
          onClick={zoomIn}
          className="shadow-lg"
          aria-label="Zoom in"
        >
          <ZoomIn className="w-5 h-5 text-white" />
        </Button>
      </div>

      <div
        className="absolute inset-0 overflow-auto"
        style={{ top: getTopOffset() }}
      >
        {computedSize && src ? (
          <div
            className="relative mx-auto px-4"
            style={{ width: computedSize.width, height: computedSize.height }}
          >
            <img
              src={src}
              alt="uploaded file"
              className="block select-none absolute inset-0"
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
